import { createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../../axios';
import { InvoiceCreateData, InvoiceData } from '../types/invoiceTypes';

// Utility function to ensure consistent response handling
const handleApiResponse = (response: any) => {
  if (response.data?.success === false) {
    throw new Error(response.data?.message || 'API request failed');
  }
  return response.data;
};

// Fetch next invoice number for a series
export const fetchNextInvoiceNumber = createAsyncThunk(
  'invoice/fetchNextNumber',
  async (series: 'MESPL' | 'MESS', { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get('/v1/admin/invoice/next-number', {
        params: { series },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch next invoice number'
      );
    }
  }
);

// Create a new invoice
export const createInvoice = createAsyncThunk(
  'invoice/createInvoice',
  async (invoiceData: InvoiceCreateData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post('/v1/admin/invoice', invoiceData);
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create invoice'
      );
    }
  }
);

// Fetch all invoices with pagination and optional filters
export const fetchInvoices = createAsyncThunk(
  'invoice/fetchInvoices',
  async (
    params: {
      page?: number;
      limit?: number;
      series?: 'MESPL' | 'MESS' | 'ALL';
      customerId?: number;
      startDate?: string;
      endDate?: string;
      excludePaymentReceived?: boolean;
      includePaymentReceived?: boolean;
      includeDuePaymentPending?: boolean;
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get('/v1/admin/invoice', {
        params,
      });
      const result = handleApiResponse(response);
      console.log('fetchInvoices API response:', result);
      return result;
    } catch (error: any) {
      console.error('fetchInvoices error:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch invoices'
      );
    }
  }
);

// You can add more invoice-related actions here in the future:
// - fetchInvoiceById
// - updateInvoice

// Fetch a single invoice by ID
export const fetchInvoiceById = createAsyncThunk(
  'invoice/fetchInvoiceById',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/admin/invoice/${invoiceId}`);
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch invoice'
      );
    }
  }
);

// Update an existing invoice
export const updateInvoice = createAsyncThunk(
  'invoice/updateInvoice',
  async (params: { invoiceId: string; data: Partial<InvoiceCreateData> }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.put(
        `/v1/admin/invoice/${params.invoiceId}`,
        params.data
      );
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update invoice'
      );
    }
  }
);

// Download invoice PDF
export const downloadInvoice = createAsyncThunk(
  'invoice/downloadInvoice',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`/v1/admin/invoice/${invoiceId}/download`, {
        responseType: 'blob', // Important: to handle binary data
      });

      // Create a blob from the response
      const blob = new Blob([response.data], { type: 'application/pdf' });

      // Create a temporary URL for the blob
      const url = window.URL.createObjectURL(blob);

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, invoiceId };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to download invoice'
      );
    }
  }
);

// Search invoices with debounce (search by customer name, M-ID, or bill number)
export const searchInvoices = createAsyncThunk(
  'invoice/searchInvoices',
  async (
    params: {
      search: string;
      page?: number;
      limit?: number;
      series?: 'MESPL' | 'MESS' | 'ALL';
      startDate?: string;
      endDate?: string;
      excludePaymentReceived?: boolean;
      includePaymentReceived?: boolean;
      includeDuePaymentPending?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await axiosInstance.get('/v1/admin/invoice', {
        params: {
          search: params.search,
          page: params.page || 1,
          limit: params.limit || 10,
            series: params.series,
             startDate: params.startDate,
             endDate: params.endDate,
             excludePaymentReceived: params.excludePaymentReceived,
             includePaymentReceived: params.includePaymentReceived,
             includeDuePaymentPending: params.includeDuePaymentPending,
           },
      });
      const result = handleApiResponse(response);
      console.log('searchInvoices API response:', result);
      return result;
    } catch (error: any) {
      console.error('searchInvoices error:', error);
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to search invoices'
      );
    }
  }
);

// Send OTP for invoice deletion (Super Admin only)
export const sendDeleteInvoiceOTP = createAsyncThunk(
  'invoice/sendDeleteOTP',
  async (invoiceId: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`/v1/admin/invoice/${invoiceId}/delete/send-otp`);
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to send OTP'
      );
    }
  }
);

// Delete invoice with OTP verification (Super Admin only)
export const deleteInvoiceWithOTP = createAsyncThunk(
  'invoice/deleteInvoice',
  async ({ invoiceId, otp }: { invoiceId: string; otp: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.delete(`/v1/admin/invoice/${invoiceId}/delete`, {
        data: { otp },
      });
      return handleApiResponse(response);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete invoice'
      );
    }
  }
);
